import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2, Download, Trash2, ImagePlus, Sparkles, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

const QUALITY_OPTIONS = [
  { key: "low", label: "Standard", hint: "fast · ~10s", proOnly: false },
  { key: "medium", label: "Premium", hint: "balanced · ~25s", proOnly: false },
  { key: "high", label: "HD", hint: "best quality · ~40s · Pro only", proOnly: true },
];

const EXAMPLE_PROMPTS = [
  "A minimalist product photo of a coffee mug with steam, on a wooden desk, soft morning light",
  "Cyberpunk skyline at sunset, neon reflections in puddles, cinematic 35mm",
  "Cute Studio Ghibli style fox sleeping under cherry blossom tree, soft pastel colors",
  "Flat vector illustration of a person working on a laptop, orange and pink palette",
];

export default function ImageProducer() {
  const { t } = useT();
  const { user } = useAuth();
  const planId = user?.plan || "free";
  const isAdmin = !!user?.is_admin;
  const isPro = isAdmin || planId === "pro";
  const hasImageAccess = isAdmin || planId === "creator" || planId === "pro";

  const [prompt, setPrompt] = useState("");
  const [quality, setQuality] = useState("medium");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [quota, setQuota] = useState(null);

  const refreshQuotaAndHistory = async () => {
    try {
      const [q, h] = await Promise.all([
        api.get("/image/quota"),
        api.get("/image/history?limit=20"),
      ]);
      setQuota(q.data);
      setHistory(h.data);
    } catch (e) {
      // 402 means locked — keep silent here
    }
  };

  useEffect(() => {
    if (!hasImageAccess) return;
    let alive = true;
    (async () => {
      try {
        const [q, h] = await Promise.all([
          api.get("/image/quota"),
          api.get("/image/history?limit=20"),
        ]);
        if (!alive) return;
        setQuota(q.data);
        setHistory(h.data);
      } catch (e) {
        // 402 means locked — keep silent here
      }
    })();
    return () => {
      alive = false;
    };
  }, [hasImageAccess]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error(t("ip_prompt_required") || "Please enter a prompt");
      return;
    }
    if (quality === "high" && !isPro) {
      toast.error(t("ip_hd_pro_only") || "HD quality is a Pro-only feature");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/image/generate", { prompt, quality });
      setResult(data);
      toast.success(t("ip_generated") || "Image generated!");
      refreshQuotaAndHistory();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || t("ip_failed") || "Generation failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = (b64, id) => {
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${b64}`;
    link.download = `lumioos-${id.slice(0, 8)}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const deleteHistoryItem = async (id) => {
    try {
      await api.delete(`/image/${id}`);
      setHistory((h) => h.filter((x) => x.id !== id));
      refreshQuotaAndHistory();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  // Free user — show upgrade gate
  if (!hasImageAccess) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-6 text-center" data-testid="image-upgrade-gate">
        <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-pink-500 items-center justify-center mb-6 shadow-lg shadow-orange-500/20">
          <Lock className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-4xl font-black tracking-tighter">
          {t("ip_locked_h") || "AI Image Producer is locked"}
        </h1>
        <p className="mt-4 text-zinc-600 max-w-xl mx-auto">
          {t("ip_locked_sub") ||
            "Generate stunning images with GPT Image 1 — product shots, illustrations, social posts. Available on Creator and Pro plans."}
        </p>
        <Link
          to="/app/billing"
          data-testid="image-upgrade-cta"
          className="mt-8 btn-accent inline-flex items-center justify-center gap-2 text-sm font-bold px-7 py-4 rounded-md"
        >
          <Sparkles className="w-4 h-4" /> {t("ip_upgrade_cta") || "Upgrade to unlock"}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-10 px-6" data-testid="image-producer">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-600 bg-orange-50 border border-orange-200 px-3 py-1.5 rounded-full">
            <ImagePlus className="w-3.5 h-3.5" />
            {t("ip_eyebrow") || "AI Image Producer"}
          </div>
          <h1 className="mt-3 text-4xl font-black tracking-tighter">
            {t("ip_h1") || "Generate images from words."}
          </h1>
          <p className="mt-2 text-zinc-600 max-w-xl">
            {t("ip_sub") ||
              "Powered by GPT Image 1 — describe anything, get a high-quality image in seconds."}
          </p>
        </div>
        {quota && (
          <div
            className="bg-white border border-amber-200 rounded-xl px-5 py-3 shadow-sm"
            data-testid="image-quota"
          >
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              {t("ip_quota") || "Quota this month"}
            </div>
            <div className="mt-1 text-2xl font-black tracking-tighter">
              {quota.limit === -1
                ? `${quota.used} / ∞`
                : `${quota.used} / ${quota.limit}`}
            </div>
          </div>
        )}
      </div>

      {/* Generator card */}
      <div className="bg-white border border-amber-200 rounded-2xl p-6 shadow-xl shadow-orange-500/5">
        <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
          {t("ip_prompt_label") || "Describe your image"}
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          maxLength={1000}
          placeholder="A serene landscape with mountains and a lake at sunset, cinematic 35mm photo..."
          data-testid="image-prompt-input"
          className="mt-2 w-full p-4 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-orange-400 focus:bg-white transition-colors resize-none"
        />

        <div className="flex flex-wrap gap-2 mt-3">
          {EXAMPLE_PROMPTS.map((ex, i) => (
            <button
              key={i}
              onClick={() => setPrompt(ex)}
              data-testid={`example-prompt-${i}`}
              className="text-[11px] text-zinc-600 bg-zinc-100 hover:bg-amber-100 border border-zinc-200 hover:border-amber-300 px-2.5 py-1 rounded-full transition-colors"
            >
              {ex.length > 50 ? ex.slice(0, 50) + "…" : ex}
            </button>
          ))}
        </div>

        <div className="mt-5">
          <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
            {t("ip_quality_label") || "Quality"}
          </label>
          <div className="grid grid-cols-3 gap-2 mt-2" data-testid="quality-options">
            {QUALITY_OPTIONS.map((q) => {
              const locked = q.proOnly && !isPro;
              return (
                <button
                  key={q.key}
                  onClick={() => !locked && setQuality(q.key)}
                  data-testid={`quality-${q.key}`}
                  disabled={locked}
                  className={`relative p-3 rounded-lg border-2 text-left transition-all ${
                    quality === q.key
                      ? "border-orange-500 bg-orange-50"
                      : locked
                      ? "border-zinc-200 bg-zinc-50 opacity-60 cursor-not-allowed"
                      : "border-zinc-200 bg-white hover:border-orange-300"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-sm">{q.label}</span>
                    {locked && <Lock className="w-3 h-3 text-zinc-400" />}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{q.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        <button
          onClick={handleGenerate}
          disabled={loading || !prompt.trim()}
          data-testid="generate-image-btn"
          className="mt-6 btn-accent w-full inline-flex items-center justify-center gap-2 font-bold py-3.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> {t("ip_generating") || "Generating…"}
            </>
          ) : (
            <>
              <Wand2 className="w-4 h-4" /> {t("ip_generate_btn") || "Generate image"}
            </>
          )}
        </button>
      </div>

      {/* Result */}
      {result && (
        <div className="mt-8 bg-white border border-amber-200 rounded-2xl p-6 shadow-xl shadow-orange-500/5" data-testid="image-result">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {t("ip_ready") || "Ready"}
            </div>
            <button
              onClick={() => downloadImage(result.image_b64, result.id)}
              data-testid="download-image-btn"
              className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-700 text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors"
            >
              <Download className="w-4 h-4" /> {t("ip_download") || "Download"}
            </button>
          </div>
          <img
            src={`data:image/png;base64,${result.image_b64}`}
            alt={result.prompt}
            className="w-full rounded-lg shadow-lg border border-zinc-200"
          />
          <p className="mt-3 text-xs text-zinc-500 italic">{result.prompt}</p>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="mt-12">
          <h2 className="text-2xl font-black tracking-tighter mb-4">
            {t("ip_history") || "Your recent generations"}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="image-history">
            {history.map((img) => (
              <div
                key={img.id}
                className="group relative bg-white border border-amber-200 rounded-lg overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                <img
                  src={`data:image/png;base64,${img.image_b64}`}
                  alt={img.prompt}
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between gap-2">
                  <p className="text-white text-[10px] line-clamp-2 flex-1">{img.prompt}</p>
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => downloadImage(img.image_b64, img.id)}
                      className="w-7 h-7 rounded bg-white/90 hover:bg-white flex items-center justify-center"
                      aria-label="Download"
                      data-testid={`download-history-${img.id}`}
                    >
                      <Download className="w-3.5 h-3.5 text-zinc-900" />
                    </button>
                    <button
                      onClick={() => deleteHistoryItem(img.id)}
                      className="w-7 h-7 rounded bg-red-500/90 hover:bg-red-500 flex items-center justify-center"
                      aria-label="Delete"
                      data-testid={`delete-history-${img.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
